import * as React from 'react'
import { Accordion, AccordionDetails } from '@mui/material'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { styled as muiStyled } from '@mui/system'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Styles from './CostBreakdownAccordion.module.css'
import Colors from 'Theme/Colors'
import IFText from 'Components/IFText/IFText'

const AccordionSummary = muiStyled(MuiAccordionSummary)(() => ({
  minHeight: '2.5rem',
  '&.Mui-expanded': {
    minHeight: '2.5rem',
  },
  '& .MuiAccordionSummary-content': {
    margin: '0px',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '0px',
  },
}))

const CostBreakdownAccordion = ({
  costBreakdown,
  currency,
  disableAccordion = false,
}) => {
  const { t } = useTranslation()

  if (!costBreakdown) return null

  const decimals = currency?.decimalPlaces ?? 2
  const formatAmount = (value) =>
    value == null ? '-' : Number(value).toFixed(decimals)

  const items = costBreakdown.items ?? []
  const { subtotal, vatTotal, total } = costBreakdown

  // Group line items by type in a fixed billing order; unknown types go last
  const typeOrder = { ENERGY: 0, TIME: 1, PARKING_TIME: 2, FLAT: 3 }
  const typeRank = (type) =>
    typeOrder[type?.toUpperCase?.()] ?? Number.MAX_SAFE_INTEGER
  const sortedItems = [...items].sort(
    (a, b) => typeRank(a.type) - typeRank(b.type),
  )

  const typeLabelKeys = {
    ENERGY: 'CostBreakdown.TypeEnergy',
    TIME: 'CostBreakdown.TypeTime',
    PARKING_TIME: 'CostBreakdown.TypeParkingTime',
    FLAT: 'CostBreakdown.TypeFlat',
  }
  const getTypeLabel = (type) => {
    const key = typeLabelKeys[type?.toUpperCase?.()]
    return key ? t(key) : type
  }

  const formatVolume = (item) => {
    const value = item.billedVolume ?? item.volume
    if (value == null) return '-'
    // Parking time is measured in hours and can carry long decimals.
    if (item.type?.toUpperCase?.() === 'PARKING_TIME') {
      return Number(value).toFixed(2)
    }
    return value
  }

  const table = (
    <div className={Styles.table}>
      {items.length > 0 && (
        <div className={[Styles.row, Styles.headerRow].join(' ')}>
          <IFText className={[Styles.cell, Styles.headerCell].join(' ')}>
            {t('CostBreakdown.Type')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.Volume')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.Rate')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.Amount')}
          </IFText>
        </div>
      )}

      {sortedItems.map((item, idx) => (
        <div className={Styles.row} key={`${item.type}-${idx}`}>
          <IFText className={Styles.cell}>{getTypeLabel(item.type)}</IFText>
          <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
            {formatVolume(item)}
          </IFText>
          <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
            {formatAmount(item.price)}
          </IFText>
          <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
            {formatAmount(item.exclVat)}
          </IFText>
        </div>
      ))}

      <div className={Styles.totals}>
        <div className={Styles.totalRow}>
          <IFText className={Styles.totalLabel}>
            {t('CostBreakdown.Subtotal')}
          </IFText>
          <IFText className={Styles.totalValue}>
            {`${formatAmount(subtotal)} ${currency?.code ?? ''}`}
          </IFText>
        </div>
        <div className={Styles.totalRow}>
          <IFText className={Styles.totalLabel}>
            {t('CostBreakdown.Vat')}
          </IFText>
          <IFText className={Styles.totalValue}>
            {`${formatAmount(vatTotal)} ${currency?.code ?? ''}`}
          </IFText>
        </div>
        <div className={Styles.totalRow}>
          <IFText className={[Styles.totalLabel, Styles.grandTotal].join(' ')}>
            {t('CostBreakdown.Total')}
          </IFText>
          <IFText className={[Styles.totalValue, Styles.grandTotal].join(' ')}>
            {`${formatAmount(total)} ${currency?.code ?? ''}`}
          </IFText>
        </div>
      </div>
    </div>
  )

  if (disableAccordion) {
    return (
      <div
        className={Styles.static}
        style={{ border: `1px solid ${Colors.DividerColor}` }}
      >
        <IFText className={[Styles.title, Styles.staticTitle].join(' ')}>
          {t('CostBreakdown.Title')}
        </IFText>
        {table}
      </div>
    )
  }

  return (
    <Accordion
      disableGutters
      className={Styles.accordion}
      sx={{
        bgcolor: 'transparent',
        color: Colors.text,
        boxShadow: 'none',
        border: `1px solid ${Colors.DividerColor}`,
        borderRadius: '5px',
        '&:before': { display: 'none' },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <IFText className={Styles.title}>{t('CostBreakdown.Title')}</IFText>
      </AccordionSummary>
      <AccordionDetails className={Styles.details}>{table}</AccordionDetails>
    </Accordion>
  )
}

CostBreakdownAccordion.propTypes = {
  costBreakdown: PropTypes.shape({
    items: PropTypes.array,
    dimensionTotals: PropTypes.array,
    subtotal: PropTypes.number,
    vatTotal: PropTypes.number,
    total: PropTypes.number,
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
    decimalPlaces: PropTypes.number,
  }),
  disableAccordion: PropTypes.bool,
}

export default CostBreakdownAccordion
